import { api, ApiError } from './client'

/**
 * R5-02 (Z-12) — firma dei contratti. Telefonia ed energia firmano sul sistema dell'operatore (il CRM registra l'esito);
 * gli altri settori usano la firma Equal: link sicuro `/c/f/...`, codice via email, PDF firmato con impronta e registro
 * delle prove. Tutto dietro il flag `firma.equal`. Nelle chiamate pubbliche token e sessione viaggiano in header.
 */
export type SignatureStatus = 'InAttesa' | 'Firmata' | 'Rifiutata' | 'Revocata' | 'Scaduta' | 'Bloccata'

export interface SignatureView {
  id: number
  provider: string
  status: SignatureStatus
  createdAt: string
  createdBy: number | null
  documentId: number | null
  documentName: string | null
  documentHash: string | null
  recipientHint: string | null
  expiresAt: string | null
  verifiedAt: string | null
  signedAt: string | null
  outcomeDate: string | null
  signedDocumentId: number | null
  signedPdfHash: string | null
  revokedAt: string | null
  note: string | null
  evidenceCount: number
}

export interface SignatureOverview {
  provider: string
  providerLabel: string
  providerKind: 'Online' | 'EsitoEsterno'
  routeSource: string
  departmentId: number | null
  departmentName: string | null
  contractSignedAt: string | null
  recipientHint: string | null
  signatures: SignatureView[]
}

export interface SignatureEvidenceRow {
  sequence: number
  action: string
  occurredAt: string
  ip: string | null
  userAgent: string | null
  details: string
  hash: string
}

export interface SignatureEvidenceView {
  signature: SignatureView
  chainValid: boolean
  events: SignatureEvidenceRow[]
}

export interface SectorRuleView {
  departmentId: number
  departmentName: string
  hasOperators: boolean
  isActive: boolean
  provider: string
  source: string
}

export interface SignatureProviderInfo { code: string; label: string; kind: 'Online' | 'EsitoEsterno' }

export const signaturesApi = {
  overview: (contractId: number) => api<SignatureOverview>(`/api/contracts/${contractId}/signature`),
  send: (contractId: number, documentId: number) =>
    api<{ id: number; url: string; inviteQueued: boolean }>(`/api/contracts/${contractId}/signature/send`, {
      method: 'POST', body: JSON.stringify({ documentId }),
    }),
  revoke: (contractId: number, id: number) =>
    api<void>(`/api/contracts/${contractId}/signature/${id}/revoke`, { method: 'POST' }),
  outcome: (contractId: number, signed: boolean, date: string, note: string | null) =>
    api<void>(`/api/contracts/${contractId}/signature/outcome`, { method: 'POST', body: JSON.stringify({ signed, date, note }) }),
  evidence: (contractId: number, id: number) =>
    api<SignatureEvidenceView>(`/api/contracts/${contractId}/signature/${id}/evidence`),
  sectorRules: () => api<{ providers: SignatureProviderInfo[]; sectors: SectorRuleView[] }>('/api/signature/sector-rules'),
  setSectorRule: (departmentId: number, provider: string | null) =>
    api<void>(`/api/signature/sector-rules/${departmentId}`, { method: 'PUT', body: JSON.stringify({ provider }) }),
}

// ── Pubblico ────────────────────────────────────────────────────────────────────────────────

const BASE = '/api/public/signature'

async function raw(token: string, session: string | null, path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers)
  headers.set('X-Signature-Link', token)
  if (session) headers.set('X-Signature-Session', session)
  if (init.body) headers.set('Content-Type', 'application/json')
  const res = await fetch(`${BASE}${path}`, { ...init, headers, credentials: 'omit', cache: 'no-store' })
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { message?: string; reason?: string }
    throw new ApiError(body.message ?? 'Errore imprevisto. Riprova.', body.reason, body, res.status)
  }
  return res
}

async function call<T>(token: string, session: string | null, path: string, init: RequestInit = {}): Promise<T> {
  return (await raw(token, session, path, init)).json() as Promise<T>
}

export interface PublicSignatureOpen {
  emailHint: string | null
  expiresAt: string | null
  signed: boolean
  practice: string | null
  documentName: string | null
}

export const publicSignatureApi = {
  open: (token: string) => call<PublicSignatureOpen>(token, null, '/open', { method: 'POST' }),
  sendOtp: (token: string) => call<{ sent: boolean }>(token, null, '/otp', { method: 'POST' }),
  verifyOtp: (token: string, code: string) =>
    call<{ session: string }>(token, null, '/otp/verify', { method: 'POST', body: JSON.stringify({ code }) }),
  document: async (token: string, session: string) => {
    const res = await raw(token, session, '/document')
    return { blob: await res.blob(), hash: res.headers.get('X-Document-Hash') }
  },
  sign: (token: string, session: string, signerName: string, accepted: boolean, place: string) =>
    call<{ signed: boolean; signedPdfHash: string }>(token, session, '/sign', {
      method: 'POST', body: JSON.stringify({ signerName, accepted, place }),
    }),
  signedCopy: async (token: string) => (await raw(token, null, '/signed-copy')).blob(),
}
